import React from "react";
import { Menu } from "lucide-react";
import { cn } from "../../lib/cn";
import { getAppVersion } from "../../lib/env";
import { Button } from "../../ui/Button";
import { Logo } from "../branding/Logo";

export type AppEnv = "dev" | "staging" | "prod" | string;

export type TopbarProps = {
  /**
   * Main page title shown on the left.
   */
  title: string;
  /**
   * Optional subtitle shown under the title (muted).
   */
  subtitle?: string;

  /**
   * Optional environment badge value (e.g. "DEV", "STAGING", "PROD").
   * If omitted, no badge is shown.
   */
  envBadge?: AppEnv;

  /**
   * Optional user string shown on the right (typically email).
   */
  userLabel?: string;

  /**
   * If true, shows the brand logo on the left.
   */
  showLogo?: boolean;

  /**
   * Optional handler to open mobile navigation (shows a mobile menu button when provided).
   */
  onMenuClick?: () => void;

  className?: string;
};

function formatEnv(env: AppEnv): {
  label: string;
  tone: "neutral" | "prod" | "staging" | "dev";
} {
  const raw = String(env ?? "").trim();
  const upper = raw.toUpperCase();

  if (upper === "PROD" || upper === "PRODUCTION") {
    return { label: "PROD", tone: "prod" };
  }

  if (upper === "STAGING" || upper === "STG") {
    return { label: "STAGING", tone: "staging" };
  }

  if (upper === "DEV" || upper === "DEVELOPMENT") {
    return { label: "DEV", tone: "dev" };
  }

  return { label: upper || "ENV", tone: "neutral" };
}

export function Topbar({
  title,
  subtitle = "Admin workspace",
  envBadge,
  userLabel,
  showLogo = false,
  onMenuClick,
  className,
}: TopbarProps) {
  const appVersion = getAppVersion();
  const env = envBadge ? formatEnv(envBadge) : null;

  return (
    <header
      className={cn(
        "flex items-center justify-between gap-3",
        "rounded-[var(--kkp-radius)]",
        "border border-[color:var(--kkp-border)]",
        "bg-[color:var(--kkp-surface)]",
        "px-4 py-3",
        className,
      )}
    >
      <div className="min-w-0 flex items-center gap-3">
        {onMenuClick ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Open navigation"
            className="md:hidden h-10 w-10 p-0 rounded-full border-2 border-[color:var(--kkp-border-strong)] ring-1 ring-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] hover:bg-[color:var(--kkp-surface-2)] shadow-[0_8px_16px_rgba(0,0,0,0.25)]"
            onClick={onMenuClick}
          >
            <Menu className="h-5 w-5" />
          </Button>
        ) : null}

        {showLogo ? <Logo height={28} alt="KamuKirim - Payroll" /> : null}

        <div className="min-w-0">
          <h1 className="truncate text-base font-bold text-[color:rgba(255,255,255,0.92)]">
            {title}
          </h1>
          {subtitle ? (
            <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {userLabel ? (
          <span className="hidden sm:inline text-xs text-[color:var(--kkp-text-muted)]">
            {userLabel}
          </span>
        ) : null}

        {env ? (
          <span
            className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold",
              "border",
              env.tone === "prod"
                ? "border-[color:rgba(34,197,94,0.35)] bg-[color:rgba(34,197,94,0.12)] text-white"
                : env.tone === "staging"
                  ? "border-[color:rgba(251,191,36,0.35)] bg-[color:rgba(251,191,36,0.12)] text-white"
                  : env.tone === "dev"
                    ? "border-[color:rgba(168,85,247,0.35)] bg-[color:rgba(168,85,247,0.12)] text-white"
                    : "border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface-2)] text-white",
            )}
            title="Environment"
          >
            {env.label} · v{appVersion}
          </span>
        ) : null}
      </div>
    </header>
  );
}
